import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, Save, X, Star } from "lucide-react";
import api from "../../lib/api";
import ImageUpload from "../../components/ImageUpload";

const SECTOR_OPTIONS = [
  "Steel Plant",
  "Petrochemical",
  "Cement",
  "Power",
  "Oil & Gas",
  "Heavy Engineering",
  "Other",
];

const empty = {
  title: "",
  sector: "Steel Plant",
  location: "",
  year: String(new Date().getFullYear()),
  code: "",
  description: "",
  image: "",
  featured: false,
  order: 0,
};

export default function ProjectsManager({ onChange: onRefresh }) {
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // null or project obj
  const [form, setForm] = useState(empty);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/projects");
      setProjects(data || []);
    } catch {
      toast.error("Could not load projects.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const startCreate = () => {
    setEditing("new");
    setForm(empty);
  };

  const startEdit = (p) => {
    setEditing(p.id);
    setForm({
      title: p.title || "",
      sector: p.sector || "Steel Plant",
      location: p.location || "",
      year: p.year || "",
      code: p.code || "",
      description: p.description || "",
      image: p.image || "",
      featured: !!p.featured,
      order: p.order || 0,
    });
  };

  const cancel = () => {
    setEditing(null);
    setForm(empty);
  };

  const onChange = (e) => {
    const { name, value, type, checked } = e.target;
    setForm({ ...form, [name]: type === "checkbox" ? checked : value });
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.image) {
      toast.error("Please upload or paste a project image.");
      return;
    }
    setSaving(true);
    try {
      const payload = { ...form, order: Number(form.order) || 0 };
      if (editing === "new") {
        await api.post("/admin/projects", payload);
        toast.success("Project created");
      } else {
        await api.patch(`/admin/projects/${editing}`, payload);
        toast.success("Project updated");
      }
      cancel();
      load();
      onRefresh && onRefresh();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const remove = async (p) => {
    if (!window.confirm(`Delete project "${p.title}"?`)) return;
    try {
      await api.delete(`/admin/projects/${p.id}`);
      toast.success("Project deleted");
      load();
      onRefresh && onRefresh();
    } catch {
      toast.error("Delete failed");
    }
  };

  if (editing !== null) {
    return (
      <div data-testid="project-editor" className="border border-[#1F3550] bg-[#0D1B2A] p-6 lg:p-8">
        <div className="flex items-center justify-between mb-6">
          <div>
            <div className="font-mono text-[10px] tracking-[0.3em] uppercase text-[#F39C12]">
              {editing === "new" ? "New Project" : "Edit Project"}
            </div>
            <h3 className="mt-1 font-heading font-bold text-xl text-[#F8FAFC]">
              {form.title || "Untitled project"}
            </h3>
          </div>
          <button
            onClick={cancel}
            data-testid="editor-cancel"
            className="inline-flex items-center gap-2 text-xs uppercase tracking-widest text-[#94A3B8] hover:text-[#F8FAFC]"
          >
            <X className="h-4 w-4" /> Cancel
          </button>
        </div>
        <form onSubmit={onSubmit} className="grid grid-cols-1 md:grid-cols-2 gap-5">
          <Field label="Title *" name="title" value={form.title} onChange={onChange} required testid="field-title" />
          <Field label="Project Code" name="code" value={form.code} onChange={onChange} placeholder="PRJ/2025/01" testid="field-code" />
          <div>
            <Label>Sector *</Label>
            <select
              name="sector"
              value={form.sector}
              onChange={onChange}
              required
              data-testid="field-sector"
              className="mt-2 w-full bg-[#050A0F] border border-[#1F3550] text-[#F8FAFC] focus:border-[#F39C12] focus:ring-1 focus:ring-[#F39C12] px-4 py-3 outline-none"
            >
              {SECTOR_OPTIONS.map((s) => <option key={s} value={s}>{s}</option>)}
            </select>
          </div>
          <Field label="Year *" name="year" value={form.year} onChange={onChange} required testid="field-year" />
          <Field label="Location *" name="location" value={form.location} onChange={onChange} required testid="field-location" />
          <Field label="Display Order" name="order" type="number" value={form.order} onChange={onChange} testid="field-order" />
          <div className="md:col-span-2">
            <ImageUpload
              label="Project Image *"
              value={form.image}
              onChange={(url) => setForm((f) => ({ ...f, image: url }))}
              testid="project-image"
            />
          </div>
          <div className="md:col-span-2">
            <Label>Description *</Label>
            <textarea
              name="description"
              value={form.description}
              onChange={onChange}
              required
              minLength={10}
              rows={5}
              data-testid="field-description"
              className="mt-2 w-full bg-[#050A0F] border border-[#1F3550] text-[#F8FAFC] focus:border-[#F39C12] focus:ring-1 focus:ring-[#F39C12] px-4 py-3 outline-none"
            />
          </div>
          <div className="md:col-span-2 flex items-center gap-3">
            <input
              type="checkbox"
              id="featured"
              name="featured"
              checked={form.featured}
              onChange={onChange}
              data-testid="field-featured"
              className="h-5 w-5 accent-[#F39C12]"
            />
            <label htmlFor="featured" className="text-sm text-[#F8FAFC]">
              Featured on Home page (shows in top 3)
            </label>
          </div>
          <div className="md:col-span-2 flex items-center gap-3 pt-2">
            <button
              type="submit"
              disabled={saving}
              data-testid="editor-save"
              className="inline-flex items-center gap-2 bg-[#F39C12] text-[#050A0F] px-6 py-3 font-bold uppercase tracking-widest text-xs hover:bg-[#D68910] transition-colors disabled:opacity-50"
            >
              <Save className="h-4 w-4" />
              {saving ? "Saving..." : (editing === "new" ? "Create Project" : "Save Changes")}
            </button>
          </div>
        </form>
      </div>
    );
  }

  return (
    <div data-testid="projects-manager">
      <div className="flex items-center justify-between mb-6">
        <div>
          <div className="font-mono text-[10px] tracking-[0.3em] uppercase text-[#F39C12]">Projects</div>
          <h2 className="mt-1 font-heading font-bold text-xl text-[#F8FAFC]">
            {projects.length} project{projects.length === 1 ? "" : "s"}
          </h2>
        </div>
        <button
          onClick={startCreate}
          data-testid="new-project-btn"
          className="inline-flex items-center gap-2 bg-[#F39C12] text-[#050A0F] px-5 py-2.5 font-bold uppercase tracking-widest text-xs hover:bg-[#D68910] transition-colors"
        >
          <Plus className="h-4 w-4" /> New Project
        </button>
      </div>

      {loading ? (
        <div className="py-20 text-center text-[#94A3B8]">Loading...</div>
      ) : projects.length === 0 ? (
        <div className="py-20 text-center text-[#94A3B8] border border-dashed border-[#1F3550]">
          No projects yet. Click "New Project" to add your first one.
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {projects.map((p) => (
            <div
              key={p.id}
              data-testid={`project-row-${p.id}`}
              className="border border-[#1F3550] bg-[#0D1B2A] overflow-hidden flex flex-col"
            >
              <div className="relative aspect-[4/3]">
                <img src={p.image} alt={p.title} className="w-full h-full object-cover" />
                {p.featured && (
                  <div className="absolute top-3 left-3 bg-[#F39C12] text-[#050A0F] text-[9px] uppercase tracking-widest font-bold px-2 py-1 flex items-center gap-1">
                    <Star className="h-3 w-3 fill-[#050A0F]" /> Featured
                  </div>
                )}
              </div>
              <div className="p-5 flex-1 flex flex-col">
                <div className="font-mono text-[10px] tracking-[0.25em] uppercase text-[#94A3B8]">
                  {p.sector} · {p.year} {p.code ? `· ${p.code}` : ""}
                </div>
                <h3 className="mt-2 font-heading font-bold text-[#F8FAFC] leading-tight">{p.title}</h3>
                <p className="mt-2 text-sm text-[#94A3B8] line-clamp-2">{p.description}</p>
                <div className="mt-auto pt-4 flex items-center gap-2">
                  <button
                    onClick={() => startEdit(p)}
                    data-testid={`edit-${p.id}`}
                    className="flex-1 inline-flex items-center justify-center gap-2 border border-[#1F3550] hover:border-[#F39C12] text-[#F8FAFC] py-2 text-[10px] uppercase tracking-widest font-bold transition-colors"
                  >
                    <Pencil className="h-3 w-3" /> Edit
                  </button>
                  <button
                    onClick={() => remove(p)}
                    data-testid={`delete-${p.id}`}
                    className="inline-flex items-center justify-center gap-2 border border-[#1F3550] hover:border-[#EF4444] hover:text-[#EF4444] text-[#94A3B8] px-3 py-2 text-[10px] uppercase tracking-widest font-bold transition-colors"
                  >
                    <Trash2 className="h-3 w-3" />
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function Label({ children }) {
  return <label className="font-mono text-[10px] tracking-[0.3em] uppercase text-[#94A3B8]">{children}</label>;
}

function Field({ label, testid, ...props }) {
  return (
    <div>
      <Label>{label}</Label>
      <input
        data-testid={testid}
        {...props}
        className="mt-2 w-full bg-[#050A0F] border border-[#1F3550] text-[#F8FAFC] focus:border-[#F39C12] focus:ring-1 focus:ring-[#F39C12] px-4 py-3 outline-none placeholder:text-[#64748B]"
      />
    </div>
  );
}
